// The icon vocabulary (ADR 0096): one meaning maps to one Heroicons outline glyph, and
// each glyph to one meaning. This and portal-ui's vendored copy of it are the ONLY
// files in the repo allowed to import `@heroicons/vue` (scripts/check-icon-vocabulary.mjs).
// Everything else asks for an icon by meaning — `<LumiIcon name="delete" />`, or
// `LUMI_ICONS['delete']` where a component reference is needed as a value (registry
// entries, menu items).
//
// Adding a row: pick a glyph no other row uses, keep the key a meaning (not a
// place it appears), and write the label as the meaning in plain words. Then copy
// the file to portal-ui/src/icons/ — scripts/check-duplicate-copies.mjs pins it.
import type { Component } from 'vue';
import { LumiGlyphIcon } from './lumiGlyph';
import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  ArrowDownIcon,
  ArrowDownCircleIcon,
  ArrowDownOnSquareIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowRightIcon,
  ArrowRightCircleIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  AtSymbolIcon,
  BanknotesIcon,
  Bars2Icon,
  Bars3Icon,
  Bars3BottomLeftIcon,
  Bars4Icon,
  BarsArrowDownIcon,
  BeakerIcon,
  BellIcon,
  BellAlertIcon,
  BoltIcon,
  BookOpenIcon,
  BookmarkSquareIcon,
  BriefcaseIcon,
  BugAntIcon,
  BuildingLibraryIcon,
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  CalculatorIcon,
  CalendarIcon,
  CalendarDateRangeIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleOvalLeftIcon,
  CheckIcon,
  CheckCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  CircleStackIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentIcon,
  DocumentChartBarIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FingerPrintIcon,
  FolderIcon,
  FunnelIcon,
  GiftIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  HomeIcon,
  IdentificationIcon,
  InboxIcon,
  InboxArrowDownIcon,
  InboxStackIcon,
  InformationCircleIcon,
  KeyIcon,
  LanguageIcon,
  LifebuoyIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  MapIcon,
  MapPinIcon,
  MegaphoneIcon,
  NewspaperIcon,
  NoSymbolIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PauseIcon,
  PencilIcon,
  PencilSquareIcon,
  PhoneIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  PowerIcon,
  PresentationChartBarIcon,
  PresentationChartLineIcon,
  PrinterIcon,
  PuzzlePieceIcon,
  QuestionMarkCircleIcon,
  QueueListIcon,
  ReceiptRefundIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  RocketLaunchIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SignalIcon,
  Square2StackIcon,
  Square3Stack3DIcon,
  Squares2X2Icon,
  SquaresPlusIcon,
  StarIcon,
  StopIcon,
  SunIcon,
  SwatchIcon,
  TableCellsIcon,
  TagIcon,
  TicketIcon,
  TrashIcon,
  TrophyIcon,
  UserIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserMinusIcon,
  UserPlusIcon,
  UsersIcon,
  VariableIcon,
  VideoCameraIcon,
  ViewColumnsIcon,
  WindowIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/vue/24/solid';

export const LUMI_ICONS = {
  // Modules & assistant
  /** Lumi suite / all modules */
  'module.suite': Squares2X2Icon,
  /** Recruiting module */
  'module.recruiting': BriefcaseIcon,
  /** Time module */
  'module.time': ClockIcon,
  /** Apprentice module */
  'module.apprentice': AcademicCapIcon,
  /** People module */
  'module.people': UserGroupIcon,
  /** Administration (workspace admin, admin sections) */
  'module.admin': WrenchScrewdriverIcon,
  /** Lumi: the assistant, and anything Lumi wrote or suggests */
  'lumi': LumiGlyphIcon,
  // Navigation & things
  /** Home / dashboard */
  'home': HomeIcon,
  /** Applications */
  'application': InboxArrowDownIcon,
  /** An application without a posting: the candidate steps forward on their own */
  'spontaneous-application': MegaphoneIcon,
  /** Jobs / job ads */
  'job': NewspaperIcon,
  /** Published outside: external postings, careers portal */
  'posting': GlobeAltIcon,
  /** Email: inbox, send email, compose */
  'email': EnvelopeIcon,
  /** Email templates */
  'email.template': EnvelopeOpenIcon,
  /** Email settings (sender, delivery) */
  'email.sender': AtSymbolIcon,
  /** Calendar, a date, pick a date */
  'calendar': CalendarIcon,
  /** Date range / period presets */
  'date-range': CalendarDateRangeIcon,
  /** Interviews */
  'interview': VideoCameraIcon,
  /** Tasks */
  'task': ClipboardDocumentListIcon,
  /** Approve / review queue */
  'approval': ClipboardDocumentCheckIcon,
  /** Checklist (setup, onboarding) */
  'checklist': ListBulletIcon,
  /** Talent pool */
  'talent-pool': BookmarkSquareIcon,
  /** Archive */
  'archive': ArchiveBoxIcon,
  /** Templates (documents, onboarding templates) */
  'template': DocumentDuplicateIcon,
  /** Forms */
  'form': ClipboardDocumentIcon,
  /** Contracts and contract templates */
  'contract': DocumentCheckIcon,
  /** A board of cards in columns (the view switch next to the list) */
  'board': SquaresPlusIcon,
  /** A plain list of rows (the view switch next to the board) */
  'list-view': Bars4Icon,
  /** Reports */
  'report': DocumentChartBarIcon,
  /** History / earlier versions */
  'history': QueueListIcon,
  /** Figures: KPIs, workforce */
  'statistics': ChartBarIcon,
  /** System metrics */
  'metrics': PresentationChartLineIcon,
  /** Me: my profile, my data */
  'me': UserCircleIcon,
  /** Write something new: my week, a correction request, my signature, a new Lumi conversation */
  'write': PencilSquareIcon,
  /** One person */
  'person': UserIcon,
  /** People (a list of persons, team, users) */
  'people': UsersIcon,
  /** Comments / notes */
  'comment': ChatBubbleOvalLeftIcon,
  /** Company / organisation / client */
  'company': BuildingOffice2Icon,
  /** Company profile */
  'company-profile': BuildingOfficeIcon,
  /** Department / org chart */
  'department': RectangleGroupIcon,
  /** Org scenarios */
  'scenario': BeakerIcon,
  /** Position (a seat in the org chart) */
  'position': IdentificationIcon,
  /** Roles */
  'role': ShieldCheckIcon,
  /** Access rights: who can do what */
  'access': KeyIcon,
  /** Sign-in & security: SSO, passkeys, account security */
  'sign-in': FingerPrintIcon,
  /** Protected or locked: privacy, Art. 9 data, closed weeks, no access */
  'privacy': LockClosedIcon,
  /** Hidden / anonymised */
  'anonymised': EyeSlashIcon,
  /** Legal: notices, compliance, decisions */
  'legal': ScaleIcon,
  /** Statutory rules */
  'statutory-rules': BuildingLibraryIcon,
  /** Working-time account (balance) */
  'working-time-account': CalculatorIcon,
  /** Week closing */
  'week-closing': CalendarDaysIcon,
  /** My bookings (time entries) */
  'booking': Square3Stack3DIcon,
  /** Leave / absence */
  'leave': SunIcon,
  /** Delegation / acting for someone */
  'delegation': HandRaisedIcon,
  /** Projects */
  'project': FolderIcon,
  /** Activities (project time categories) */
  'activity': TagIcon,
  /** Prices, payroll */
  'money': BanknotesIcon,
  /** Wage types */
  'wage-type': TableCellsIcon,
  /** Billing & payments */
  'billing': CreditCardIcon,
  /** Refund */
  'refund': ReceiptRefundIcon,
  /** A promo or referral code redeemed at checkout */
  'promo-code': TicketIcon,
  /** Notifications */
  'notification': BellIcon,
  /** Reminders and alerts */
  'reminder': BellAlertIcon,
  /** Help & support (requests, support access) */
  'support': LifebuoyIcon,
  /** Audit log */
  'audit-log': DocumentMagnifyingGlassIcon,
  /** System / error logs */
  'system-log': BugAntIcon,
  /** AI usage, quota and logs */
  'ai-usage': CpuChipIcon,
  /** Product suggestions */
  'suggestion': LightBulbIcon,
  /** Integrations */
  'integration': PuzzlePieceIcon,
  /** Skills / skill catalogue */
  'skill': TrophyIcon,
  /** Training plan */
  'training-plan': MapIcon,
  /** Vocational school (Berufsschule) */
  'school': PresentationChartBarIcon,
  /** Toolkit / learning content */
  'toolkit': BookOpenIcon,
  /** Training occupations */
  'occupation': WrenchIcon,
  /** Workflows */
  'workflow': ArrowPathRoundedSquareIcon,
  /** Workflow state */
  'workflow.state': CubeIcon,
  /** Link / webhook */
  'webhook': LinkIcon,
  /** Event (workflow emits) */
  'event': BoltIcon,
  /** Custom fields / option lists */
  'field': VariableIcon,
  /** Languages */
  'language': LanguageIcon,
  /** Images / media library */
  'media': PhotoIcon,
  /** Branding (login screen, portal look) */
  'branding': PaintBrushIcon,
  /** Data subject requests */
  'dsar': InboxStackIcon,
  /** Data import / export */
  'import-export': ArrowsUpDownIcon,
  /** Tenant lifecycle (suspend, delete) */
  'tenant-lifecycle': PowerIcon,
  /** Data & assets section */
  'data': CircleStackIcon,
  /** Settings & preferences (any settings page) */
  'settings': Cog6ToothIcon,
  /** Job ad section: benefits */
  'benefit': GiftIcon,
  /** Job ad section: call to action */
  'call-to-action': RocketLaunchIcon,
  /** Address / location */
  'location': MapPinIcon,
  /** Employment type (full / part time) */
  'employment-type': ChartPieIcon,
  /** Attachments */
  'attachment': PaperClipIcon,
  /** Take a photo with the device camera */
  'camera': CameraIcon,
  /** A file / PDF */
  'file': DocumentIcon,
  /** Field type: text */
  'text-field': Bars3BottomLeftIcon,
  /** Phone / call */
  'phone': PhoneIcon,
  /** My inbox (workspace home card) */
  'inbox': InboxIcon,
  /** A navigation section without an icon of its own */
  'section': RectangleStackIcon,
  // Actions
  /** Add / create / new */
  'add': PlusIcon,
  /** Delete for good */
  'delete': TrashIcon,
  /** Close / take out / clear / dismiss (nothing is destroyed) */
  'remove': XMarkIcon,
  /** Edit */
  'edit': PencilIcon,
  /** Save */
  'save': ArrowDownOnSquareIcon,
  /** Confirm / accept / ticked */
  'confirm': CheckIcon,
  /** Done / approved / success */
  'done': CheckCircleIcon,
  /** Reject / deactivate */
  'reject': XCircleIcon,
  /** Blocked / cannot */
  'blocked': NoSymbolIcon,
  /** Send / invite / resend */
  'send': PaperAirplaneIcon,
  /** Reply / undo / reopen / restore */
  'reply': ArrowUturnLeftIcon,
  /** Refresh / retry / replace */
  'refresh': ArrowPathIcon,
  /** Search */
  'search': MagnifyingGlassIcon,
  /** Filter */
  'filter': FunnelIcon,
  /** Sort */
  'sort': BarsArrowDownIcon,
  /** Download / export a file */
  'download': ArrowDownTrayIcon,
  /** Upload / import a file / change photo */
  'upload': ArrowUpTrayIcon,
  /** Opens somewhere else (new tab, other app) */
  'open-external': ArrowTopRightOnSquareIcon,
  /** Show / preview / view details */
  'view': EyeIcon,
  /** Compare side by side */
  'compare': ArrowsRightLeftIcon,
  /** Duplicate / copy */
  'duplicate': Square2StackIcon,
  /** Move up */
  'move-up': ArrowUpIcon,
  /** Move down */
  'move-down': ArrowDownIcon,
  /** Drag to reorder */
  'reorder': Bars2Icon,
  /** Favourite / star / vote (solid = on) */
  'favourite': StarIcon,
  /** Print */
  'print': PrinterIcon,
  /** Clock in / start timer / play */
  'clock-in': PlayIcon,
  /** Clock out / stop timer */
  'clock-out': StopIcon,
  /** Pause / break / suspend */
  'pause': PauseIcon,
  /** Sign out */
  'sign-out': ArrowRightStartOnRectangleIcon,
  /** Remove someone from a seat / access */
  'vacate': UserMinusIcon,
  /** Mark as new hire */
  'new-hire': UserPlusIcon,
  /** Zoom out */
  'zoom-out': MagnifyingGlassMinusIcon,
  /** Zoom in */
  'zoom-in': MagnifyingGlassPlusIcon,
  /** Fit to view / expand view */
  'fit': ArrowsPointingOutIcon,
  /** Shrink view */
  'shrink': ArrowsPointingInIcon,
  /** Dock panel / layout half–full */
  'dock': ViewColumnsIcon,
  /** Float panel */
  'float': WindowIcon,
  /** Helpful */
  'helpful': HandThumbUpIcon,
  /** Not helpful */
  'not-helpful': HandThumbDownIcon,
  /** Jump to new answer */
  'jump-down': ArrowDownCircleIcon,
  // Status
  /** Error / missing / needs checking */
  'error': ExclamationCircleIcon,
  /** Warning / rule violation */
  'warning': ExclamationTriangleIcon,
  /** Info / explanation */
  'info': InformationCircleIcon,
  /** Open question */
  'question': QuestionMarkCircleIcon,
  /** Live / postable */
  'live': SignalIcon,
  /** Moved on to the next stage */
  'moved-on': ArrowRightCircleIcon,
  // Controls
  /** Expand / open a dropdown or section */
  'expand': ChevronDownIcon,
  /** Collapse */
  'collapse': ChevronUpIcon,
  /** Next / forward / drill in / submenu */
  'next': ChevronRightIcon,
  /** Previous / back */
  'previous': ChevronLeftIcon,
  /** From → to (a value changes) */
  'flow': ArrowRightIcon,
  /** More actions */
  'more': EllipsisHorizontalIcon,
  /** Open menu (phone) */
  'menu': Bars3Icon,
  /** Switch (workspace, role, select) */
  'switcher': ChevronUpDownIcon,
  /** Fold / unfold sidebar */
  'fold-sidebar': ChevronDoubleLeftIcon,
  /** Appearance: light / dark theme */
  'theme': SwatchIcon,
} as const satisfies Record<string, Component>;

/** Every meaning the product has an icon for. */
export type LumiIconName = keyof typeof LUMI_ICONS;

/**
 * The filled `on` form of a meaning, where it has one. A variant of the same
 * meaning, never a second key: `favourite` is the outline star, and the solid star
 * says it is switched on.
 */
export const LUMI_ICONS_ON: Partial<Record<LumiIconName, Component>> = {
  favourite: StarSolidIcon,
};
